import { MagicWand, Info, CaretDown, CaretUp } from '@phosphor-icons/react';
import {
  useCallback,
  useEffect,
  useState,
  useRef,
  useLayoutEffect,
} from 'react';
import { AutoCompleteSelectBox } from 'src/components/AutoCompleteSelectBox';
import { Button } from 'src/components/Button';
import { Input } from 'src/components/Input';
import { Label } from 'src/components/Label';
import { Loading } from 'src/components/Loading';
import { Modal } from 'src/components/Modal';
import { PromptTextArea } from 'src/components/PromptTextArea';
import { SegmentedButtons } from 'src/components/SegmentedButtons';
import { SkillChip } from 'src/components/SkillChip';
import { Textarea } from 'src/components/Textarea';
import { Tooltip } from 'src/components/Tooltip';
import { AGENT_PROMPT_VARIABLES } from 'src/constants';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Agent, Skill } from 'src/interfaces/agent.interface';
import { Prompt } from 'src/interfaces/prompt.interface';
import { QueryObject } from 'src/interfaces/queryObject.interface';
import { Voice } from 'src/interfaces/voice.interface';
import { AgentVoiceFemaleStub, AgentVoiceMaleStub } from 'src/stubs/agent.stub';
import { extractVariables } from 'src/utils/extractVariables';
import {
  REQUIRED_AGENT_CONFIG_FOR_SKILLS,
  SKILL_DEFAULT_CONFIG,
  SKILL_OPTIONS,
} from './Skills/constants';
import { InformationCollectionSkill } from './Skills/InformationCollectionSkill';
import { SearchSkill } from './Skills/SearchSkill';

type BasicInformationProps = {
  agent: Agent;
  setAgent: (value: React.SetStateAction<Agent>) => void;
  loading: boolean;
  defaultPrompt: Prompt | undefined;
  promptGenerating: boolean;
  promptStreaming: boolean;
  onPromptContentChange: (content: string) => void;
  onGeneratePrompt: (input: string) => Promise<void> | void;
};

const SKILLS_COLLAPSED_HEIGHT_REM = 6;
const SKILLS_COLLAPSED_HEIGHT_PX = SKILLS_COLLAPSED_HEIGHT_REM * 16;

export const BasicInformation = ({
  agent,
  setAgent,
  loading = false,
  defaultPrompt,
  promptGenerating,
  promptStreaming,
  onPromptContentChange,
  onGeneratePrompt,
}: BasicInformationProps) => {
  const { get } = useAuthedRequest();
  let defaultVoiceType: 'male' | 'female' | '' = '';
  if (!agent.voice || agent.voice === AgentVoiceMaleStub) {
    defaultVoiceType = 'male';
  } else if (agent.voice === AgentVoiceFemaleStub) {
    defaultVoiceType = 'female';
  }
  const [voiceType, setVoiceType] = useState<'male' | 'female' | ''>(
    defaultVoiceType,
  );

  useEffect(() => {
    handleChangeVoiceType(voiceType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceType]);

  const [skillModalOpen, setSkillModalOpen] = useState(false);
  const [isSkillsExpanded, setIsSkillsExpanded] = useState(false);
  const [skillsContentHeight, setSkillsContentHeight] = useState<string>(
    `${SKILLS_COLLAPSED_HEIGHT_REM}rem`,
  );
  const skillsRef = useRef<HTMLDivElement>(null);
  const [selectedSkill, setSelectedSkill] = useState<Skill | null>(null);
  const [deleteSkillModalOpen, setDeleteSkillModalOpen] = useState(false);
  const [generatePromptModalOpen, setGeneratePromptModalOpen] = useState(false);
  const [promptGeneratorInput, setPromptGeneratorInput] = useState('');
  const endpoint = agent.id ? `agents/update?id=${agent.id}` : 'agents/create';

  const searchPrompts = useCallback(
    async (content: string) => {
      const queryObject: QueryObject = {
        filters: JSON.stringify({
          ...(content.length !== 0 && { content }),
          type: 'prompt',
          scope: 'global|user',
        }),
      };
      const query = new URLSearchParams(queryObject as Record<string, string>);

      const data = await get(`/prompts?${query.toString()}`);

      return data.items.map((prompt: Prompt) => ({
        value: prompt.id,
        label: prompt.content,
      }));
    },
    [get],
  );

  const updateAgentSkills = (skills: Skill[]) => {
    const enable_mcp = skills.some((s) =>
      REQUIRED_AGENT_CONFIG_FOR_SKILLS.some(
        (r) => r.type === s.type && r.config.enable_mcp,
      ),
    );
    setAgent((prev) => ({ ...prev, skills, enable_mcp }));
  };

  const handleChangeVoiceType = (value: 'male' | 'female' | '') => {
    if (value === 'male') {
      setAgent({ ...agent, voice: AgentVoiceMaleStub as Voice });
    } else if (value === 'female') {
      setAgent({ ...agent, voice: AgentVoiceFemaleStub as Voice });
    }
  };

  const handleSkillClick = (skill: Skill) => {
    setSelectedSkill(skill);
    setSkillModalOpen(true);
  };

  const handleRemoveSkill = (skillType: string) => {
    const updatedSkills = agent.skills?.filter((s) => s.type !== skillType);
    updateAgentSkills(updatedSkills ?? []);
    setSelectedSkill(null);
    setSkillModalOpen(false);
    setDeleteSkillModalOpen(false);
  };

  const availableSkills = Object.values(SKILL_OPTIONS).filter(
    (skill) => !agent.skills?.some((s) => s.type === skill.type),
  );

  const getSkillTitle = (skillType: string) => {
    const skillOption = SKILL_OPTIONS[skillType];
    return skillOption?.name || skillType;
  };

  const modalSkillButtonDisabled = () => {
    if (!selectedSkill || !SKILL_OPTIONS[selectedSkill.type]) return true;
    return !SKILL_OPTIONS[selectedSkill.type].validator(selectedSkill.config);
  };

  const skillSelected = (skillType: string) => {
    const skill = agent.skills?.find((s) => s.type === skillType);
    if (!skill || !SKILL_OPTIONS[skillType]) return false;
    return SKILL_OPTIONS[skillType].validator(skill.config);
  };

  // Get current prompt content based on switcher
  const getCurrentPromptContent = (): string => {
    if (typeof agent.prompt === 'string') {
      return agent.prompt;
    } else if (
      agent.prompt &&
      typeof agent.prompt === 'object' &&
      'content' in agent.prompt
    ) {
      return agent.prompt.content ?? '';
    }
    return '';
  };

  const SkillComponent = SKILL_OPTIONS[selectedSkill?.type ?? '']?.component;

  useLayoutEffect(() => {
    if (skillsRef.current) {
      const scrollHeight = skillsRef.current.scrollHeight;
      if (isSkillsExpanded) {
        setSkillsContentHeight(`${scrollHeight}px`);
      } else {
        setSkillsContentHeight(`${SKILLS_COLLAPSED_HEIGHT_REM}rem`);
      }
    }
  }, [agent.skills, availableSkills, isSkillsExpanded]);

  return (
    <div className="flex flex-col gap-2">
      {endpoint === 'agents/create' && (
        <div className="mb-4">
          <Label required>Voice Type</Label>
          <SegmentedButtons
            value={voiceType}
            onValueChange={(value) => setVoiceType(value as 'male' | 'female')}
            buttons={[
              { value: 'male', label: 'Male' },
              { value: 'female', label: 'Female' },
            ]}
            className="mt-2"
          />
        </div>
      )}
      <div className="mb-4">
        <Input
          label={
            <div className="flex items-center">
              <span className="mr-1">Initial Message</span>{' '}
              <Tooltip
                content="When enabled, the initial message will always be played. This is strongly recommended for inbound calls and optional for outbound calls."
                position="right"
              >
                <Info size={16} />
              </Tooltip>
            </div>
          }
          type="text"
          id="initialMessage"
          placeholder="Hi {name}, this is Fluents AI. How can I help you today?"
          value={agent.initial_message ?? ''}
          onChange={(e) =>
            setAgent({ ...agent, initial_message: e.target.value })
          }
          disabled={loading || (!agent.user_id && !!agent.id)}
        />
      </div>
      <div className="mb-4">
        <Input
          label={
            <div className="flex items-center">
              <span className="mr-1">Voicemail Message</span>{' '}
              <Tooltip
                content="Only triggers for outbound calls when VM detection set to continue. Best results when configured to detect machine beep"
                position="right"
              >
                <Info size={16} />
              </Tooltip>
            </div>
          }
          type="text"
          id="voicemailMessage"
          placeholder="It looks like I missed you. I'll try again later."
          value={agent.voicemail_message ?? ''}
          onChange={(e) =>
            setAgent({ ...agent, voicemail_message: e.target.value })
          }
          disabled={loading || (!agent.user_id && !!agent.id)}
        />
      </div>
      <div className="mb-4">
        <div className="flex items-center">
          <span className="mr-1">Choose Skills</span>{' '}
          <Tooltip
            content="Defines specific capabilities agent can perform, such as booking or transferring. Use to handle structured user requests."
            position="right"
          >
            <Info size={16} />
          </Tooltip>
        </div>

        <div className="flex flex-col mt-2">
          <div
            ref={skillsRef}
            className="flex flex-wrap gap-2 overflow-hidden transition-all duration-300 ease-in-out"
            style={{ maxHeight: skillsContentHeight }}
          >
            {[...(agent.skills ?? []), ...availableSkills]
              .sort((a, b) => {
                const aIndex = Object.keys(SKILL_OPTIONS).findIndex(
                  (s) => s === a.type,
                );
                const bIndex = Object.keys(SKILL_OPTIONS).findIndex(
                  (s) => s === b.type,
                );
                return aIndex - bIndex;
              })
              .map((skill) => (
                <SkillChip
                  key={skill.type}
                  label={getSkillTitle(skill.type)}
                  tooltip={SKILL_OPTIONS[skill.type]?.tooltip || ''}
                  selected={skillSelected(skill.type)}
                  onClick={() => {
                    if (agent.skills?.some((s) => s.type === skill.type)) {
                      const existingSkill = agent.skills.find(
                        (s) => s.type === skill.type,
                      );
                      if (existingSkill) {
                        handleSkillClick(existingSkill);
                      }
                    } else {
                      const newSkillConfig = {
                        ...(SKILL_DEFAULT_CONFIG[skill.type] || {}),
                      };
                      const newSkill = {
                        type: skill.type,
                        config: newSkillConfig,
                      };
                      // Do not persist the new skill yet; open modal to configure it.
                      handleSkillClick(newSkill);
                    }
                  }}
                  onRemove={() => {
                    setSelectedSkill({
                      type: skill.type,
                      config: {},
                    } as Skill);
                    setDeleteSkillModalOpen(true);
                  }}
                />
              ))}
          </div>
          {skillsRef.current &&
            skillsRef.current.scrollHeight > SKILLS_COLLAPSED_HEIGHT_PX && (
              <Button
                onClick={() => setIsSkillsExpanded(!isSkillsExpanded)}
                variant="text"
                size="small"
                className="mt-2 flex items-center self-start"
              >
                {isSkillsExpanded ? (
                  <>
                    Show Less <CaretUp className="ml-1" size={14} />
                  </>
                ) : (
                  <>
                    Show More <CaretDown className="ml-1" size={14} />
                  </>
                )}
              </Button>
            )}
        </div>
      </div>
      <div className="mb-3">
        <div className="flex flex-row items-center justify-between w-full">
          <div className="flex items-center">
            <span className="mr-1 required-field">Agent Prompt</span>
            <Tooltip
              content="Defines the behavior and instructions for the agent during customer calls."
              position="right"
            >
              <Info size={16} />
            </Tooltip>
          </div>
          <div className="flex items-center gap-2 text-sm text-neutral-600">
            <Button
              variant="outlined"
              color="primary"
              size="medium"
              fontWeight="medium"
              className="py-2"
              disabled={loading || promptGenerating || promptStreaming}
              onClick={() => {
                setGeneratePromptModalOpen(true);
              }}
            >
              <MagicWand size={20} />
              &nbsp;Generate Prompt
            </Button>
          </div>
        </div>

        <div className="w-full mt-3 2xl:mt-4">
          <AutoCompleteSelectBox
            key={defaultPrompt?.id}
            defaultValue={
              defaultPrompt?.id
                ? {
                    value: defaultPrompt.id,
                    label: defaultPrompt.content,
                  }
                : undefined
            }
            fetchOptions={searchPrompts}
            onChange={(value) => setAgent({ ...agent, prompt: value })}
            className="w-full"
            variant="outlined"
            color="primary"
            size="medium"
            disabled={loading || (!agent.user_id && !!agent.id)}
          />
        </div>
      </div>

      <div className="mb-6 col-span-2">
        <div className="relative">
          <PromptTextArea
            required
            value={getCurrentPromptContent()}
            onChange={(e) => onPromptContentChange(e.target.value)}
            loading={loading || promptGenerating || promptStreaming}
            variables={Array.from(
              new Set([
                ...AGENT_PROMPT_VARIABLES,
                ...extractVariables(getCurrentPromptContent()),
              ]),
            )}
            placeholder={
              'Provide context, personality, and specific instructions for how your agent should behave, respond, and handle different scenarios during customer calls.'
            }
            rows={12}
            autoScroll={promptStreaming}
          />
          {promptGenerating && (
            <div className="absolute top-0 right-0 w-full h-full bg-neutral-0 flex flex-col justify-center items-center">
              <div className="flex flex-col justify-center items-center gap-2">
                <Loading size="16" className="overflow-hidden" />
                <p>Generating prompt...</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Skill Configuration Modal */}
      <Modal
        title={
          selectedSkill
            ? `Configure ${getSkillTitle(selectedSkill.type)}`
            : 'Configure Skill'
        }
        isOpen={skillModalOpen}
        onClose={() => {
          setSkillModalOpen(false);
          setSelectedSkill(null);
        }}
        actionButton={
          selectedSkill && (
            <Button
              onClick={() => {
                setSkillModalOpen(false);
                const isExisting = agent.skills?.some(
                  (s) => s.type === selectedSkill.type,
                );
                const updatedSkills = isExisting
                  ? agent.skills?.map((s) =>
                      s.type === selectedSkill.type ? selectedSkill : s,
                    )
                  : [...(agent.skills ?? []), selectedSkill];
                updateAgentSkills(updatedSkills ?? []);
                setSelectedSkill(null);
              }}
              disabled={modalSkillButtonDisabled()}
            >
              Save
            </Button>
          )
        }
      >
        {selectedSkill && (
          <div className="flex flex-col">
            <div className="flex-1">
              {SKILL_OPTIONS[selectedSkill.type] && SkillComponent && (
                <SkillComponent
                  config={selectedSkill.config}
                  onConfigChange={(config) => {
                    setSelectedSkill({
                      ...selectedSkill,
                      config: config as object,
                    });
                  }}
                />
              )}
              {/* Below are skills we don't want to allow users to add. Only display if skill
              was added manually to the agent. */}
              {selectedSkill.type === 'collect' && (
                <InformationCollectionSkill
                  config={selectedSkill.config}
                  onConfigChange={(config) => {
                    setSelectedSkill({
                      ...selectedSkill,
                      config,
                    } as Skill);
                  }}
                />
              )}
              {selectedSkill.type === 'search' && (
                <SearchSkill
                  config={selectedSkill.config}
                  onConfigChange={(config) => {
                    setSelectedSkill({
                      ...selectedSkill,
                      config,
                    } as Skill);
                  }}
                />
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Modal delete skill confirmation */}
      <Modal
        title="Delete Skill"
        isOpen={deleteSkillModalOpen}
        onClose={() => setDeleteSkillModalOpen(false)}
        className="md:w-96"
        actionButton={
          <Button
            variant="outlined"
            color="error"
            onClick={() =>
              selectedSkill && handleRemoveSkill(selectedSkill.type)
            }
          >
            Delete Skill
          </Button>
        }
      >
        <div className="flex flex-col gap-4">
          <p>Are you sure you want to delete this Skill?</p>
        </div>
      </Modal>

      <Modal
        title="Generate Agent Prompt"
        isOpen={generatePromptModalOpen}
        onClose={() => setGeneratePromptModalOpen(false)}
        actionButton={
          <Button
            onClick={async () => {
              setGeneratePromptModalOpen(false);
              try {
                await onGeneratePrompt(promptGeneratorInput);
              } finally {
                setPromptGeneratorInput('');
              }
            }}
          >
            Generate
          </Button>
        }
      >
        <Textarea
          placeholder={`What do you want this agent to do, including its role, specific tasks, and overall objective with as much detail as possible?`}
          value={promptGeneratorInput}
          onChange={(e) => setPromptGeneratorInput(e.target.value)}
          rows={6}
          className="resize-none"
        />
      </Modal>
    </div>
  );
};
